/**
 * @file DateTime input type - extends String with datetime formatting, timezone support
 * @version 1.0.0
 */

import { StringInputType } from './string.js';

export const DateTimeInputType = {
  name: 'datetime',
  matches: getAttribute => getAttribute('format') === 'date-time',
  displayComponent: {
    name: 'DateTimeDisplay',
    extends: StringInputType.displayComponent,
    methods: {
      formatDisplayValue(value) {
        if (!value) return '';
        const date = new Date(value);

        const timezone = this.getAttribute('timezone');
        if (timezone) {
          try {
            const formatted = date.toLocaleString('sv-SE', {
              timeZone: timezone,
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            });
            return `${formatted} (${timezone})`;
          } catch (error) {
            console.warn(
              `Invalid timezone ${timezone}, falling back to local time`
            );
          }
        }

        return date.toLocaleString('sv-SE', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        });
      },
    },
  },
  editComponent: {
    name: 'DateTimeEdit',
    extends: StringInputType.editComponent,
    data() {
      return {
        getEditValue(value) {
          if (!value) return '';

          const date = new Date(value);
          const timezone = this.getAttribute('timezone');

          if (timezone) {
            try {
              // Convert UTC to the specified timezone for editing
              const formatter = new Intl.DateTimeFormat('sv-SE', {
                timeZone: timezone,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              });

              const parts = formatter.formatToParts(date);
              const year = parts.find(p => p.type === 'year').value;
              const month = parts.find(p => p.type === 'month').value;
              const day = parts.find(p => p.type === 'day').value;
              const hour = parts.find(p => p.type === 'hour').value;
              const minute = parts.find(p => p.type === 'minute').value;

              return `${year}-${month}-${day}T${hour}:${minute}`;
            } catch (error) {
              console.warn(
                `Error converting datetime for timezone ${timezone}:`,
                error
              );
            }
          }

          // Default - convert ISO string to local datetime-local format for HTML input
          const offset = date.getTimezoneOffset() * 60000;
          const localDate = new Date(date.getTime() - offset);
          return localDate.toISOString().slice(0, 16);
        },
      };
    },
    methods: {
      getInputType() {
        return 'datetime-local';
      },
      parseInputValue(event) {
        const inputValue = event.target.value;
        if (!inputValue) return '';

        const timezone = this.getAttribute('timezone');
        if (timezone) {
          try {
            // The input value is interpreted as being in the specified timezone
            // We need to convert it to UTC for storage

            // Create a date assuming user input is in UTC
            const inputDate = new Date(inputValue + ':00Z');

            // Create a temporary date in the target timezone to find offset
            const tempFormatter = new Intl.DateTimeFormat('sv-SE', {
              timeZone: timezone,
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            });

            // Get the current date in target timezone
            const tzLocalDateString = tempFormatter.format(inputDate);
            const shiftedDate = new Date(tzLocalDateString + 'Z');

            const offsetMs = shiftedDate.getTime() - inputDate.getTime();

            // Apply this offset to our input
            inputDate.setTime(inputDate.getTime() - offsetMs);

            return inputDate.toISOString();
          } catch (error) {
            console.warn(
              `Error parsing datetime for timezone ${timezone}:`,
              error
            );
          }
        }

        // Default behavior - parse as local time and convert to ISO (UTC) for storage
        const localDate = new Date(inputValue);
        return localDate.toISOString();
      },
    },
    template: `
      <div class="d-flex flex-wrap gap-2 align-items-center">
        <input
          :type="inputType"
          class="form-control form-control-sm"
          style="width: auto"
          :class="{ 'is-invalid': validationError }"
          :value="getEditValue(value)"
          v-bind="inputAttributes"
          @input="handleInput"
          @keydown="$emit('keydown', $event)"
          @blur="$emit('blur', $event)"
        />
        <small v-if="getAttribute('timezone')" class="text-muted">{{ getAttribute('timezone') }}</small>
      </div>
    `,
  },
};

export default DateTimeInputType;
